import { PlusIcon, SlidersHorizontalIcon } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { openListSettings } from 'app/components/lists/ListSettingsDialog'
import { PersonalListMarker } from 'app/components/lists/PersonalListMarker'
import { NavRow } from 'app/components/sidebar/NavRow'
import { NavSectionHeader } from 'app/components/sidebar/NavSectionHeader'
import { Button } from 'app/components/ui/button'
import type { useHomepage } from 'app/hooks/useHomepage'
import type { useNavLists } from 'app/hooks/useNavLists'
import { useOpenNavListEditor } from 'app/hooks/useOpenNavListEditor'
import { useListStore } from 'app/stores/StoreContext'
import type { ListSettingsTarget } from 'app/types/list-settings'
import type { NavListEntry } from 'app/types/navigation'
import { useHandleSearchParams } from 'app/utils'
import { requestColumnEditor } from 'lib/columnEditorRequest'
import { afterListDeletedPath, manualListPath } from 'lib/lists'
import { navListVisibility } from 'lib/navLists'
import { RECORD_TYPES } from 'lib/recordTypes'

export const ListsSection = observer(function ListsSection({
  lists,
  homepage,
  collapsed,
  onToggleCollapsed,
  onListsChanged,
  onNewList,
  onManage,
}: {
  lists: ReturnType<typeof useNavLists>
  homepage: ReturnType<typeof useHomepage>
  collapsed: boolean
  onToggleCollapsed: () => void
  onListsChanged: () => void
  onNewList: () => void
  onManage: () => void
}): ReactElement {
  const openNavListEditor = useOpenNavListEditor()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const handleSearchParams = useHandleSearchParams()
  const location = useLocation()
  const rows = collapsed ? lists.entries.filter((entry) => entry.item.active) : lists.visible
  const workspaceQuery = searchParams.has(`wId`) ? `?wId=${searchParams.get(`wId`)}` : ``
  const listStore = useListStore()

  // Only the page that just lost its record has to move; every other page is still valid.
  const leaveDeletedList = (listId: string) => {
    if (location.pathname !== manualListPath(listId)) return
    navigate(afterListDeletedPath(listId, listStore.hits, lists.setting.order, workspaceQuery))
  }

  // Every contact list the List API holds gets Settings; a saved list of another record type keeps Edit list.
  const settingsTarget = (entry: NavListEntry): ListSettingsTarget | undefined =>
    entry.source === `system` && entry.def
      ? { source: `system`, def: entry.def }
      : entry.list && !entry.customPage && (entry.source === `manual` || entry.source === `custom`)
        ? { source: `practice`, list: entry.list }
        : undefined

  const openRowSettings = (target: ListSettingsTarget) =>
    openListSettings({
      target,
      onChanged: onListsChanged,
      onDeleted: () => {
        if (target.source === `practice`) leaveDeletedList(target.list.id)
      },
      onEditColumns: () => {
        const slug = target.source === `system` ? target.def.slug : target.list.id
        // Already on the list: only the view changes, so its band, filters and query stay.
        if (location.pathname === manualListPath(slug)) handleSearchParams({ view: `table` })
        else navigate(`${manualListPath(slug, `table`)}${workspaceQuery.replace(`?`, `&`)}`)
        requestColumnEditor(`/lists/${slug}`)
      },
    })

  return (
    <section aria-label="Lists" className="flex flex-col">
      <NavSectionHeader
        label="Lists"
        collapsed={collapsed}
        onToggle={onToggleCollapsed}
        action={
          <Button variant="icon" size="sm" aria-label="New list" className="h-6 w-6" onClick={onNewList}>
            <PlusIcon size={14} weight="bold" className="text-subtle" />
          </Button>
        }
      />
      {rows.map((entry) => {
        const target = settingsTarget(entry)
        return (
          <NavRow
            key={entry.key}
            item={{ ...entry.item, icon: RECORD_TYPES[entry.recordType].icon }}
            homepage={homepage}
            muted
            marker={<PersonalListMarker personal={navListVisibility(entry) === `personal`} />}
            customPage={entry.customPage}
            list={entry.list}
            onEditList={
              entry.customPage
                ? () => openNavListEditor(entry, { onSaved: onListsChanged, onDeleted: onListsChanged })
                : undefined
            }
            onSettings={target && (() => openRowSettings(target))}
          />
        )
      })}
      {!collapsed && (lists.overflowCount > 0 || lists.canCollapse) && (
        <Button
          variant="ghost"
          // oxlint-disable-next-line shadcn/no-restyle -- pl-13 aligns the label under NavRow's icon+text start; no Button size matches that offset
          className="w-full justify-start pl-13"
          disabled={!lists.settingLoaded}
          onClick={() => lists.save({ ...lists.setting, showAll: !lists.setting.showAll })}
        >
          <span className="text-label-sm text-subtle">
            {lists.canCollapse ? `Show fewer` : `Show all (${lists.overflowCount})`}
          </span>
        </Button>
      )}
      {!collapsed && (
        <Button variant="ghost" className="w-full justify-start" disabled={!lists.settingLoaded} onClick={onManage}>
          <SlidersHorizontalIcon size={16} weight="bold" className="text-subtle" aria-hidden="true" />
          <span className="text-label-sm text-subtle">Manage lists</span>
        </Button>
      )}
    </section>
  )
})
